import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AuthenticationService } from '..';
import {
	BookingPoolCountsDocument,
	AddAndConfirmBookingsToFlightDocument,
	AddBookingsToFlightDocument,
	AddPassengersDocument,
	BookingCountersDocument,
	BookingFilterCountsDocument,
	BookingPickerExpiredCountDocument,
	BookingSearchCountsDocument,
	BookingFilter,
	BookingInput,
	BookingsPreferredDateDocument,
	ConfirmBookingToFlightDocument,
	CreateBookingDocument,
	DeleteBookingDocument,
	DeletePassengerDocument,
	GetBookingDocument,
	GetFlightDocument,
	GetHistoricalBookingsDocument,
	GetMobileFlightDocument,
	GetUpcomingBookingsDocument,
	InviteBookingsToFlightDocument,
	LocationInput,
	MarkBookingPaidDocument,
	OrderByBooking,
	OrderDirection,
	PassengerCountersDocument,
	PassengerInput,
	PreferredDateInput,
	RemoveBookingFromFlightDocument,
	SaveBookingDocument,
	SavePassengerDocument,
	UpdateBookingCommentsDocument,
	DashboardBookingsDocument,
	GetBookingLogsDocument,
	GetFirstBookingWithFlightDocument,
	GetBookingMailsDocument,
	BookingPaymentInput,
	SaveBookingPaymentDocument,
	PassengerPaymentInput,
	SavePassengerPaymentDocument,
	PaymentType,
	UpdatePassengerPaymentStatusDocument,
	PaymentStatus,
	UpdateBookingPaymentDocument,
	GetBookingsOfFlightDocument,
	GetBookingForPaymentFormDocument,
	Tag,
	UpdateBookingTagsDocument,
	GetTagsOfTypeWithCountersDocument,
	TagType,
	GetUpcomingBookingsIsDocument,
	GetHistoricalBookingsIsDocument,
	GetBookingPassengerEmailsDocument,
	Booking,
	MergeBookingsDocument,
	DeleteLogDocument,
	PreferredFlightInput,
	UpdateBookingPreferredFlightsDocument,
	BookingStatus,
	CheckinPassengersDocument,
	GetBookingFlightOptionsDocument,
	SendCheckinNotificationDocument,
} from '../../graphql/generated/gen-types';
import { collection, doc, Firestore, getDoc, limit, onSnapshot, orderBy, query, QueryConstraint, where } from '@angular/fire/firestore';
import { TenantSettingService } from '../tenantSetting/tenant-setting.service';

@Injectable({
	providedIn: 'root',
})
export class BookingService {
	currentUser;

	//InfiniteScroll
	private _storedBookings: Booking[] = [];
	private _bookingTableScrollY: number = 0;

	private saveBookingClick = new Subject();
	saveBookingClick$ = this.saveBookingClick.asObservable();

	// Emits once the booking-save mutation triggered by save() has actually completed, so a
	// caller can sequence a follow-up write on the same booking instead of racing it (#112).
	private bookingSaved = new Subject<void>();
	bookingSaved$ = this.bookingSaved.asObservable();

	private dropBooking = new BehaviorSubject(false);
	dropBooking$ = this.dropBooking.asObservable();

	private refreshSearchResults = new Subject();
	refreshSearchResults$ = this.refreshSearchResults.asObservable();

	constructor(
		private apollo: Apollo,
		private tenantSettingService: TenantSettingService,
		private authenticationService: AuthenticationService,
		private firestore: Firestore,
	) {
		this.authenticationService.currentUser.subscribe((user) => {
			this.currentUser = user;
		});
	}
	// communicate between list and table to refresh the search results
	public refreshSearchResultsClick() {
		this.refreshSearchResults.next(null);
	}

	public dropBookingRefetch() {
		this.dropBooking.next(false);
	}

	public getBooking(id: any): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetBookingDocument,
			variables: {
				id,
			},
			fetchPolicy: 'cache-and-network',
		});
	}
	public getBookingFlightOptions(id: any): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetBookingFlightOptionsDocument,
			variables: {
				id,
			},
			fetchPolicy: 'cache-and-network',
		});
	}
	public getFirstBookingWithFlight(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetFirstBookingWithFlightDocument,
		});
	}

	public getBookingLogs(id: any): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetBookingLogsDocument,
			variables: {
				id,
			},
			fetchPolicy: 'network-only',
		});
	}

	public getBookingMails(id: any): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetBookingMailsDocument,
			variables: {
				id,
			},
			fetchPolicy: 'network-only',
		});
	}

	public getUpcomingBookings(
		orderBy?: OrderByBooking,
		orderDirection?: OrderDirection,
		limit?: number,
		cursor?: string,
		filter?: string[],
		bookingFilter?: BookingFilter,
		flightId?: string,
		flightOnlyMatching?: boolean,
	): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetUpcomingBookingsDocument,
			variables: {
				orderBy,
				orderDirection,
				limit,
				cursor,
				filter,
				bookingFilter,
				flightId,
				flightOnlyMatching,
			},
			fetchPolicy: 'network-only',
		});
	}

	public getHistoricalBookings(
		orderBy?: OrderByBooking,
		orderDirection?: OrderDirection,
		limit?: number,
		cursor?: string,
		filter?: string[],
		bookingFilter?: BookingFilter,
	): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetHistoricalBookingsDocument,
			variables: {
				orderBy,
				orderDirection,
				limit,
				cursor,
				filter,
				bookingFilter,
			},
			fetchPolicy: 'network-only',
		});
	}

	public getBookingsIS(
		type: string,
		limit?: number,
		cursor?: string,
		filter?: string[],
		orderBy?: OrderByBooking,
		orderDirection?: OrderDirection,
		bookingFilter?: BookingFilter,
		flightId?: string,
		flightOnlyMatching?: boolean,
		endAt?: boolean,
		includeExpired?: boolean,
		openAmountOnly?: boolean,
	): QueryRef<any> {
		let showUpcoming = true;
		let bookingQuery = GetUpcomingBookingsIsDocument;
		// 235: the bookingFilter decides the set; showUpcoming is sent consistently for older backends
		if (type === 'historical' || bookingFilter === BookingFilter.History) {
			bookingQuery = GetHistoricalBookingsIsDocument;
			showUpcoming = false;
		}
		return this.apollo.watchQuery({
			query: bookingQuery,
			variables: {
				showUpcoming: showUpcoming,
				limit: limit,
				cursor: cursor,
				filter: filter,
				orderBy: orderBy,
				orderDirection: orderDirection,
				bookingFilter: bookingFilter,
				flightId: flightId,
				flightOnlyMatching: flightOnlyMatching,
				endAt: endAt,
				includeExpired: includeExpired,
				openAmountOnly: openAmountOnly ?? null,
			},
			fetchPolicy: 'network-only',
		});
	}

	// 235 FR-012: how many bookings match a search in Active / Expired / Completed & cancelled
	// (v3: with the Open amount chip and the selected tags)
	public bookingSearchCounts(filter: string[], openAmountOnly = false) {
		return this.apollo.query<any>({
			query: BookingSearchCountsDocument,
			variables: { filter, openAmountOnly },
			fetchPolicy: 'network-only',
		});
	}

	// 235 v3: counts of the filter row (Open amount chip + per-tag counts) for a status (+ search)
	// fresh: bypass the server's 30 s cache (after the operator's own edits / an explicit refresh)
	public bookingFilterCounts(bookingFilter: BookingFilter, openAmountOnly: boolean, filter: string[], fresh = false) {
		return this.apollo.query<any>({
			query: BookingFilterCountsDocument,
			variables: { bookingFilter, openAmountOnly, filter, fresh },
			fetchPolicy: 'network-only',
		});
	}

	// 235 FR-017: bookings the flight picker hides because they expire before the flight date
	public bookingPickerExpiredCount(flightId: string, flightOnlyMatching: boolean, filter: string[]) {
		return this.apollo.query<any>({
			query: BookingPickerExpiredCountDocument,
			variables: { flightId, flightOnlyMatching, filter },
			fetchPolicy: 'network-only',
		});
	}

	public getBookingPassengerEmails(
		showUpcoming: boolean,
		filter: string[],
		bookingFilter?: BookingFilter,
		openAmountOnly = false,
	) {
		return this.apollo.query<any>({
			query: GetBookingPassengerEmailsDocument,
			variables: {
				showUpcoming,
				filter,
				bookingFilter,
				openAmountOnly,
			},
			fetchPolicy: 'network-only',
		});
	}

	public getDashboardBookings(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: DashboardBookingsDocument,
			fetchPolicy: 'network-only',
		});
	}

	public getBookingsPreferredDate(preferredDateInput: PreferredDateInput): QueryRef<any> {
		return this.apollo.watchQuery({
			query: BookingsPreferredDateDocument,
			variables: {
				preferredDateInput,
			},
			fetchPolicy: 'network-only',
		});
	}

public passengerCounters(passengers: any): QueryRef<any> {
		return this.apollo.watchQuery({
			query: PassengerCountersDocument,
			variables: {
				passengers,
			},
		});
	}

	public bookingCounters(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: BookingCountersDocument,
			fetchPolicy: 'network-only',
		});
	}

	public save() {
		this.saveBookingClick.next(null);
	}

	public notifyBookingSaved() {
		this.bookingSaved.next();
	}

	public saveBooking(bookingInput: BookingInput, bookingId: string, locationInput: LocationInput): Observable<any> {
		if (bookingId) {
			return this.apollo
				.mutate<any>({
					mutation: SaveBookingDocument,
					variables: {
						bookingInput,
						bookingId,
						locationInput,
					},
					refetchQueries: [
						{
							query: GetBookingDocument,
							variables: {
								id: bookingId,
							},
						},
					],
				})
				.pipe(map((result: any) => result.data));
		} else {
			return this.apollo
				.mutate<any>({
					mutation: SaveBookingDocument,
					variables: { bookingInput, bookingId, locationInput },
				})
				.pipe(map((result: any) => result.data));
		}
	}

	public delete(bookingId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: DeleteBookingDocument,
				variables: {
					bookingId,
				},
			})
			.pipe(map((result: any) => result.data));
	}
	public deleteLog(bookingId: string, logId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: DeleteLogDocument,
				variables: {
					bookingId,
					logId,
				},
				refetchQueries: [
					{
						query: GetBookingLogsDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public sendCheckinNotification(bookingId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SendCheckinNotificationDocument,
				variables: {
					bookingId,
				},
				update(cache, { data }) {
					if (data?.sendCheckinNotification) {
						const normalizedId = cache.identify({ __typename: 'Booking', id: bookingId });
						cache.modify({
							id: normalizedId,
							fields: {
								checkinNotifiedAt: () => data.sendCheckinNotification,
							},
						});
					}
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public merge(bookingId1: string, bookingId2: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: MergeBookingsDocument,
				variables: {
					bookingIdFrom: bookingId1,
					bookingIdTo: bookingId2,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId2,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public createBooking(flightId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: CreateBookingDocument,
				variables: {
					flightId,
				},
				refetchQueries: [
					{
						query: GetMobileFlightDocument,
						variables: { flightId },
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public savePassenger(passengerInput: PassengerInput, passengerId: string, bookingId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SavePassengerDocument,
				variables: {
					passengerInput,
					passengerId,
					bookingId,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public saveMobilePassenger(passengerInput: PassengerInput, passengerId: string, bookingId: string, flightId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SavePassengerDocument,
				variables: {
					passengerInput,
					passengerId,
					bookingId,
					// 112: mark this save as a mobile check-in save so weight corrections get logged
					checkinFlightId: flightId,
				},
				refetchQueries: [
					{
						query: GetMobileFlightDocument,
						variables: {
							flightId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public deletePassenger(passengerId: string, bookingId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: DeletePassengerDocument,
				variables: {
					passengerId,
					bookingId,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public addPassengers(bookingId: string, count: number) {
		return this.apollo
			.mutate<any>({
				mutation: AddPassengersDocument,
				variables: {
					bookingId,
					count,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}
	public checkInPassengers(bookingId: string) {
		return this.apollo
			.mutate<any>({
				mutation: CheckinPassengersDocument,
				variables: {
					bookingId,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public updateBookingComments(bookingId: string, comments: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateBookingCommentsDocument,
				variables: {
					bookingId,
					comments,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: { id: bookingId },
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public updatePreferredFlights(bookingId: string, preferredFlights: [PreferredFlightInput]): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateBookingPreferredFlightsDocument,
				variables: {
					bookingId,
					preferredFlights,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: { id: bookingId },
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public updateBookingTags(bookingId: string, tags: Tag[] = []): Observable<any> {
		const tagIds: string[] = tags.map((tag) => tag.id);
		return this.apollo
			.mutate<any>({
				mutation: UpdateBookingTagsDocument,
				variables: {
					bookingId: bookingId,
					tags: tagIds,
				},
				refetchQueries: [
					{
						query: GetTagsOfTypeWithCountersDocument,
						variables: {
							tagType: TagType.Booking,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public markBookingPaid(bookingId: string) {
		return this.apollo
			.mutate<any>({
				mutation: MarkBookingPaidDocument,
				variables: {
					bookingId,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public addBookingsToFlight(bookings: [any], flightId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: AddBookingsToFlightDocument,
				variables: {
					bookings: bookings.map((booking) => booking.id),
					flightId,
				},
				refetchQueries: [
					{
						query: GetFlightDocument,
						variables: {
							flightId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public inviteBookingsToFlight(bookings: [any], flightId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: InviteBookingsToFlightDocument,
				variables: {
					bookings: bookings.map((booking) => booking.id),
					flightId,
				},
				refetchQueries: [
					{
						query: GetFlightDocument,
						variables: {
							flightId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public addAndConfirmBookingsToFlight(bookings: [any], flightId: string): Observable<any> {
		this.dropBooking.next(true);
		return this.apollo
			.mutate<any>({
				mutation: AddAndConfirmBookingsToFlightDocument,
				variables: {
					bookings: bookings.map((booking) => booking.id),
					flightId,
				},
				refetchQueries: [
					{
						query: GetFlightDocument,
						variables: {
							flightId,
						},
					},
					{
						query: DashboardBookingsDocument,
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	// 228: weekplanner pool chip counts (no search; the pool counts the loaded set when searching)
	public bookingPoolCounts(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: BookingPoolCountsDocument,
			fetchPolicy: 'network-only',
		});
	}

	public confirmBookingToFlight(bookingId: string, flightId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: ConfirmBookingToFlightDocument,
				variables: {
					bookingId,
				},
				refetchQueries: [
					{
						query: GetFlightDocument,
						variables: {
							flightId,
						},
					},
					{
						query: DashboardBookingsDocument,
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public removeBookingFromFlight(bookingId: string, flightId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: RemoveBookingFromFlightDocument,
				variables: {
					bookingId,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
					{
						query: GetFlightDocument,
						variables: {
							flightId,
						},
					},
					{
						query: DashboardBookingsDocument,
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public removeFlightFromBookingDetail(bookingId: string, flightId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: RemoveBookingFromFlightDocument,
				variables: {
					bookingId,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public saveBookingPayments(bookingId: string, bookingPaymentInput: BookingPaymentInput): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SaveBookingPaymentDocument,
				variables: {
					bookingId,
					bookingPaymentInput,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public savePassengerPayments(bookingId: string, passengerId: string, passengerPaymentInput: PassengerPaymentInput): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SavePassengerPaymentDocument,
				variables: {
					bookingId,
					passengerId,
					passengerPaymentInput,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public updatePassengerPaymentStatus(bookingId: string, passengerId: string, paymentStatus: PaymentStatus): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdatePassengerPaymentStatusDocument,
				variables: {
					bookingId,
					passengerId,
					paymentStatus,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public updateBookingPayment(bookingId: string, paymentType: PaymentType): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateBookingPaymentDocument,
				variables: {
					bookingId,
					paymentType,
				},
				refetchQueries: [
					{
						query: GetBookingDocument,
						variables: {
							id: bookingId,
						},
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public getBookingsOfFlight(flightId: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetBookingsOfFlightDocument,
			variables: {
				flightId,
			},
		});
	}

	public getBookingForPaymentForm(id: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetBookingForPaymentFormDocument,
			variables: {
				id,
			},
		});
	}

	// FIRESTORE QUERY
	// Teardown matters here: tenantSetting re-emits on every cache update and the previous
	// implementation installed a NEW permanent onSnapshot listener per emission, per call —
	// leaked listeners re-fired on every booking write and stormed the backend (#165)
	public getBookingConversations(history: number, flightId?: string, unread?: Boolean): Observable<Booking[]> {
		return new Observable<Booking[]>((subscriber) => {
			subscriber.next([]);
			let snapshotUnsubscribe: (() => void) | null = null;
			let closed = false;
			const settingsSub = this.tenantSettingService
				.getTenantSetting()
				.valueChanges.pipe(take(1))
				.subscribe(async (result) => {
					let tenantId = result.data.tenantSetting?.tenantId;

					const collectionName = `/tenants/${tenantId}/booking/`;
					const constraints = [];

					// filter on flight id if provided
					let flightRef = null;
					if (flightId) {
						flightRef = (await getDoc(doc(this.firestore, `/tenants/${tenantId}/flight/${flightId}`))).ref;
						constraints.push(where('flight.id', '==', flightRef));
					}
					if (unread) {
						constraints.push(where('smsUnread', '>', 0));
					}
					// always order by date
					constraints.push(where('removed', '==', null));
					// limit the number of results if no flightId is provided
					if (!flightId && !unread && history > 0) {
						constraints.push(orderBy('smsLatest.date', OrderDirection.Desc));
						constraints.push(limit(history));
					}
					const collection_ = collection(this.firestore, collectionName);
					const query_ = query(collection_, ...constraints);

					if (closed) return;
					snapshotUnsubscribe = onSnapshot(
						query_,
						(snapshot) => {
							subscriber.next(
								snapshot.docs.map((doc) => {
									const booking = { ...doc.data(), smsType: 'BOO', id: doc.id } as Booking;
									if (booking.smsLatest) {
										booking.smsLatest.date = booking.smsLatest?.date?.toDate();
										// summarize the text for the SMS preview
										booking.smsLatest.summary =
											booking.smsLatest?.text?.length > 25 ? booking.smsLatest?.text?.substring(0, 35) + '...' : booking.smsLatest?.text;
									}
									return booking;
								}),
							);
						},
						(error) => {
							console.error('Error getting booking conversations', error);
						},
					);
				});
			return () => {
				closed = true;
				settingsSub.unsubscribe();
				if (snapshotUnsubscribe) snapshotUnsubscribe();
			};
		});
	}
	private bookingsToConfirmSubject: BehaviorSubject<number> | null = null;
	public bookingsToConfirm(): Observable<number> {
		if (!this.bookingsToConfirmSubject) {
			// Check if subject already exists
			this.bookingsToConfirmSubject = new BehaviorSubject<number>(0);

			this.tenantSettingService
				.getTenantSetting()
				.valueChanges.pipe(
					take(1), // Only take the first emitted value from valueChanges
					switchMap((result) => {
						let tenantId = result.data?.tenantSetting?.tenantId; // Safe navigation
						if (tenantId) {
							const bookingCollection = collection(this.firestore, `tenants/${tenantId}/booking`);
							const bookingConstraints: QueryConstraint[] = [where('removed', '==', null), where('status', '==', BookingStatus.Adde)];
							const query_ = query(bookingCollection, ...bookingConstraints);
							return new Observable<number>((observer) => {
								const unsubscribe = onSnapshot(
									query_,
									(snapshot) => {
										observer.next(snapshot.size);
									},
									(error) => {
										observer.error(error);
									},
								);
								return () => unsubscribe(); // Return unsubscribe function
							});
						} else {
							return of(0); // Handle cases where tenantId is null/undefined
						}
					}),
					shareReplay(1), // Share the single stream with multiple subscribers
				)
				.subscribe(this.bookingsToConfirmSubject);
		}
		return this.bookingsToConfirmSubject.asObservable();
	}
}
